import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { isMobile as isMobileDevice } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';
import xss from 'xss';

import { Skeleton } from '@pkg/components';

import { EidCategory, type Tag } from '@app/client';

import CharacterCounter from '../CharacterCounter/CharacterCounter';
import EditIndicator from '../EditIndicator/EditIndicator';
import { ReactComponent as AddIcon } from '../icons/add-icon-purple.svg';

import './_YatPageBio.scss';

interface YatPageBioProps {
    currentEmojiTags: Tag[];
    editable: boolean;
    updateTagsQueue: (eidCategory: EidCategory, data: string, deleteHashes?: string[]) => void;
    isLoading: boolean;
}
const maxCharLimit = 140;
const category = EidCategory.EMOJI_DESCRIPTION;

const YatPageBio = ({ currentEmojiTags, editable, updateTagsQueue, isLoading }: YatPageBioProps) => {
    const { t } = useTranslation(['yat_page']);
    const placeHolderText = t('description_placeholder');
    const [description, setDescription] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [showIndicator, setShowIndicator] = useState(false);
    const [currentCharCount, setCurrentCharCount] = useState(0);

    useEffect(() => {
        const currentDescription = [...currentEmojiTags].filter((t) => t.tag === category).pop();
        if (currentDescription) {
            const currentDescriptionData = xss(currentDescription.data);
            setDescription(currentDescriptionData);
            setCurrentCharCount(currentDescriptionData.length);
        }
    }, [currentEmojiTags]);

    const addButton = useMemo(() => {
        if (editable && !description && !isEditing) {
            return (
                <span
                    className="yat-page-bio__btn--add"
                    onClick={() => {
                        setIsEditing(true);
                    }}
                >
                    <AddIcon />
                    {t('description_button')}
                </span>
            );
        }
    }, [editable, description, isEditing, t]);

    const handleChange = (e) => {
        const { value } = e.target;

        //textarea maxLength was inconsistent on all browsers
        //handling character count here instead
        // so all the browsers input the same line break for the char count
        const truncatedVal = value.replace(/\r?\n/gm, '\n').substr(0, maxCharLimit); // so you can still paste something longer than the maxCharLimit
        setCurrentCharCount(truncatedVal.length);
        setDescription(truncatedVal);
    };

    const onBlurred = useCallback(() => {
        const deleteTags = currentEmojiTags.filter((t) => t.tag === category).map((t) => t.hash);
        //If they haven't typed in any value or backspaced it then just delete the tag
        updateTagsQueue(category, description, deleteTags);
        setIsEditing(false);
    }, [currentEmojiTags, description, updateTagsQueue]);

    //Show nothing when it's being viewed and has no bio
    if (!editable && !description) {
        return null;
    }

    const showAddButton = editable && !description && !isEditing;
    return (
        <Skeleton on={isLoading} variant="bio">
            <div className="yat-page-bio">
                <div className="yat-page-bio__indicators">
                    {isEditing ? (
                        <CharacterCounter maxChars={maxCharLimit} currentChars={currentCharCount} />
                    ) : (
                        <EditIndicator
                            show={!showAddButton && !isMobileDevice && showIndicator}
                            fieldName={t('words:description')}
                        />
                    )}
                </div>
                {addButton}
                {editable && (isEditing || description) ? (
                    <TextareaAutosize
                        onMouseEnter={() => setShowIndicator(true)}
                        onMouseLeave={() => setShowIndicator(false)}
                        className="yat-page-bio__textarea"
                        value={description}
                        rows={3}
                        onChange={(e) => handleChange(e)}
                        placeholder={placeHolderText}
                        onBlur={() => onBlurred()}
                        onFocus={() => setIsEditing(true)}
                        disabled={false}
                    />
                ) : null}
                {!editable ? (
                    <TextareaAutosize className="yat-page-bio__textarea" disabled={true} value={description} />
                ) : null}
            </div>
        </Skeleton>
    );
};

export default YatPageBio;
